import positiveNavStyles from '../styles/index.css?inline';
import { createProductIcon } from './product-icon.js';
import { createPositiveLogo } from './positive-logo.js';
import { createInterfaceIcon } from './interface-icon.js';
import { createProductPreview } from './product-preview.js';
import { createProductPanels } from './product-panels.js';
import { createCompactNavigation } from './compact-navigation.js';
import { createAgentOnboarding } from './agent-onboarding.js';
import { createTranslator } from '../i18n/index.js';
import { registerFont } from '../core/font.js';
import { resolveAgentOnboarding } from '../core/agent-onboarding.js';
import { updateNavigationLink, isControlActivation } from '../core/navigation.js';

const tagName = 'positive-nav';

/** Register on init in the target document; imports do not require a DOM. */
export function registerPositiveNav(window) {
  if (!window?.customElements || !window.HTMLElement) {
    throw new Error('[PositiveNav] The target document must support Web Components.');
  }
  registerFont(window.document);
  const existing = window.customElements.get(tagName);
  if (existing) {
    if (typeof existing.prototype.render !== 'function' || typeof existing.prototype.getBarHeight !== 'function'
      || typeof existing.prototype.dispose !== 'function' || typeof existing.prototype.setActionHandler !== 'function'
      || typeof existing.prototype.openPanel !== 'function' || typeof existing.prototype.setPanelHandler !== 'function'
      || typeof existing.prototype.setTrackHandler !== 'function' || typeof existing.prototype.performAgentAction !== 'function') {
      throw new Error('[PositiveNav] The positive-nav name is already registered by another component.');
    }
    return;
  }

  let nextId = 0;
  class PositiveNavElement extends window.HTMLElement {
    constructor() {
      super();
      this.instanceId = `positive-nav-${++nextId}`;
      this._items = new Map();
      // Two breakpoint listeners; no observer or continuous height measurement.
      this._compactQuery = window.matchMedia('(max-width: 1023px)');
      this._mobileQuery = window.matchMedia('(max-width: 767px)');
      const root = this.attachShadow({ mode: 'open' });
      const style = this.ownerDocument.createElement('style');
      style.textContent = positiveNavStyles;
      this._bar = this.ownerDocument.createElement('nav');
      this._bar.className = 'positive-nav-bar';
      this._bar.id = `${this.instanceId}-bar`;
      this._bar.setAttribute('aria-label', 'Positive');
      this._bar.tabIndex = -1;
      const button = (className, action, source) => {
        const node = this.ownerDocument.createElement('button');
        node.type = 'button';
        node.className = `positive-nav-button ${className}`;
        node.dataset.action = action;
        node.dataset.source = source;
        return node;
      };
      const separator = (className) => {
        const node = this.ownerDocument.createElement('span');
        node.className = `positive-nav-separator ${className}`;
        node.setAttribute('aria-hidden', 'true');
        return node;
      };
      this._brand = button('positive-nav-brand', 'openCatalog', 'brand');
      this._brand.append(createPositiveLogo(this.ownerDocument));
      this._brandSeparator = separator('positive-nav-brand-separator');
      this._apps = this.ownerDocument.createElement('div');
      this._apps.className = 'positive-nav-products positive-nav-apps';
      this._apps.setAttribute('role', 'group');
      this._discoverSeparator = separator('positive-nav-discover-separator');
      this._discover = this.ownerDocument.createElement('div');
      this._discover.className = 'positive-nav-products positive-nav-discover';
      this._discover.setAttribute('role', 'group');
      this._discoverLabel = this.ownerDocument.createElement('span');
      this._discoverLabel.className = 'positive-nav-discover-label';
      this._discover.append(this._discoverLabel);
      this._allProducts = button('positive-nav-all-products', 'openCatalog', 'allProducts');
      this._allProductsLabel = this.ownerDocument.createElement('span');
      this._allProductsLabel.className = 'positive-nav-all-products-label';
      const catalogIcon = createInterfaceIcon('apps', this.ownerDocument);
      catalogIcon.classList.add('positive-nav-interface-icon');
      this._allProducts.append(catalogIcon, this._allProductsLabel);
      this._compact = createCompactNavigation(this.ownerDocument);
      this._agentOnboarding = createAgentOnboarding(this, {
        onAction: (action, event) => this._actionHandler?.(action, event),
        onTrack: (payload) => this._trackHandler?.(payload),
        onError: (error) => this._errorHandler?.(error),
        restoreFocus: () => this._restoreInvoker(this._allProducts),
        isMobile: () => this._mobileQuery.matches,
        onOpenChange: (open) => {
          if (this._disposed) return;
          if (open && this._panels?.element.open) this._panels.close({ reason: 'switch', restoreFocus: false });
          this._preview?.suspend(open || this._compactQuery.matches || !!this._panels?.element.open);
        },
      });
      const actions = this.ownerDocument.createElement('div');
      actions.className = 'positive-nav-bar-actions';
      actions.append(this._agentOnboarding.element, this._allProducts, this._compact.others);
      this._bar.append(this._brand, this._brandSeparator, this._apps,
        this._discoverSeparator, this._discover, this._compact.element, actions);
      this._handleClick = (event) => {
        const trigger = event.target.closest?.('[data-action]');
        if (!trigger || !this._bar.contains(trigger) || event.defaultPrevented || !isControlActivation(event, trigger)) return;
        this._preview.close({ dismiss: true });
        if (trigger.dataset.source === 'agentOnboarding') {
          const action = this._agentOnboarding.action(trigger.dataset.action);
          if (action) this._actionHandler?.(action, event);
          return;
        }
        if (trigger.dataset.action === 'openCatalog') {
          this._actionHandler?.({ action: 'openCatalog', source: trigger.dataset.source }, event);
          return;
        }
        const product = this._catalog?.visibleProducts.find(({ id }) => id === trigger.dataset.productId);
        if (!product || product.isCurrent) return;
        const action = product.isConnected
          ? { action: 'navigate', source: 'product', productId: product.id, url: product.urls.app, target: '_blank' }
         : { action: 'openProduct', source: 'product', productId: product.id };
        this._actionHandler?.(action, event);
      };
      this._bar.addEventListener('click', this._handleClick);
      this._bar.addEventListener('auxclick', this._handleClick);
      root.append(style, this._bar, this._agentOnboarding.popup);
      this._preview = createProductPreview(this, (action, event) => this._actionHandler?.(action, event));
      this._panels = createProductPanels(this, {
        onAction: (action, event) => this._actionHandler?.(action, event),
        onLifecycle: (name, payload) => this._panelHandler?.(name, payload),
        isMobile: () => this._mobileQuery.matches,
        restoreInvoker: (trigger) => this._restoreInvoker(trigger),
        onModalChange: (open) => {
          if (open) this._agentOnboarding.close({ reason: 'catalog' });
          this._preview.suspend(open || this._compactQuery.matches || this._agentOnboarding.isOpen);
          for (const trigger of this._catalogTriggers) trigger.setAttribute('aria-expanded', String(open));
        },
      });
      root.append(this._agentOnboarding.sheet);
      this._catalogTriggers = [this._brand, this._allProducts, this._compact.others];
      for (const trigger of this._catalogTriggers) {
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', this._panels.element.id);
        trigger.setAttribute('aria-expanded', 'false');
      }
      this._handleResponsiveChange = () => {
        if (this._disposed) return;
        // CSS may hide the focused element before the browser
        // delivers the breakpoint event.
        const focused = root.activeElement ?? this._lastFocused;
        const inPreview = this._preview.element.contains(focused);
        this._preview.suspend(this._compactQuery.matches || this._panels.element.open || this._agentOnboarding.isOpen);
        this._panels.refreshLayout(focused);
        this._agentOnboarding.refreshLayout(focused);
        if (!this._panels.element.open && focused && (this._bar.contains(focused) || inPreview)
          && !focused.getClientRects().length) this._restoreInvoker(this._compactQuery.matches ? this._compact.others: this._allProducts);
      };
      this._handleFocusIn = (event) => { this._lastFocused = event.target; };
      this._handleFocusOut = () => {
        const focused = this._lastFocused;
        window.queueMicrotask(() => {
          if (!this._disposed && !root.activeElement && this._lastFocused === focused
            && (!focused?.isConnected || focused.getClientRects().length)) this._lastFocused = undefined;
        });
      };
      root.addEventListener('focusin', this._handleFocusIn);
      root.addEventListener('focusout', this._handleFocusOut);
    }

    render(options, catalog) {
      this.lang = options.locale;
      this.dataset.theme = options.theme;
      this.dataset.position = options.style.position;
      this._catalog = catalog;
      const focused = this.shadowRoot.activeElement;
      const t = createTranslator(options.locale);
      const label = (key, values) => t(`nav.${key}`, values);
      this._brand.title = label('brandProducts');
      this._brand.setAttribute('aria-label', label('brandProducts'));
      this._apps.setAttribute('aria-label', label('connectedApps'));
      this._discover.setAttribute('aria-label', label('discover'));
      this._discoverLabel.textContent = label('discover');
      this._allProductsLabel.textContent = label('allProducts');
      this._compact.update(catalog, t);
      this._agentOnboarding.update(options, resolveAgentOnboarding(catalog.currentProduct, options.agentOnboardingUrl), t);
      // An analytics callback can render newer options or destroy the instance.
      if (this._disposed || this._catalog !== catalog) return;
      if (this._zIndex !== options.style.zIndex) {
        if (options.style.zIndex === undefined) this.style.removeProperty('--positive-nav-z-index');
        else this.style.setProperty('--positive-nav-z-index', String(options.style.zIndex));
        this._zIndex = options.style.zIndex;
      }

      const visibleIds = new Set(catalog.visibleProducts.map(({ id }) => id));
      let restoreBarFocus = false;
      let replacementFocus;
      for (const [id, item] of this._items) {
        if (visibleIds.has(id)) continue;
        restoreBarFocus ||= item === focused;
        item.removeAttribute('href');
        item.remove();
        this._items.delete(id);
      }

      for (const product of catalog.visibleProducts) {
        let item = this._items.get(product.id);
        const tag = product.isCurrent ? 'span': product.isConnected ? 'a': 'button';
        if (!item || item.localName !== tag) {
          const previous = item;
          item = this.ownerDocument.createElement(tag);
          item.className = 'positive-nav-product';
          if (!product.isCurrent) {
            if (tag === 'button') item.type = 'button';
            item.classList.add('positive-nav-button');
            item.dataset.action = 'product';
            item.dataset.source = 'product';
          }
          item.id = `${this.instanceId}-${product.id}`;
          item.dataset.productId = product.id;
          if (previous) {
            item.append(...previous.childNodes);
            if (previous === focused) replacementFocus = item;
            previous.removeAttribute('href');
            previous.replaceWith(item);
          } else {
            const icon = this.ownerDocument.createElement('span');
            icon.className = 'positive-nav-product-icon';
            icon.append(createProductIcon(product.logoId, this.ownerDocument));
            const name = this.ownerDocument.createElement('span');
            name.className = 'positive-nav-product-name';
            item.append(icon, name);
          }
          this._items.set(product.id, item);
        }
        item.dataset.status = product.status;
        if (tag === 'a') updateNavigationLink(item, { url: product.urls.app, source: 'product' }, catalog.currentProduct?.id);
        const name = item.querySelector('.positive-nav-product-name');
        name.textContent = product.name;
        name.hidden = !product.isCurrent;
        if (product.isCurrent) item.title = label('currentProduct', { product: product.name });
        else item.removeAttribute('title');
        if (product.isCurrent) item.setAttribute('aria-current', 'page');
        else item.removeAttribute('aria-current');
        if (product.isCurrent) {
          item.removeAttribute('aria-label');
          item.removeAttribute('aria-controls');
          item.removeAttribute('aria-expanded');
          item.removeAttribute('aria-haspopup');
        } else {
          item.setAttribute('aria-label', label(product.isConnected ? 'openApp': 'discoverProduct', { product: product.name }));
          item.setAttribute('aria-controls', this._preview.element.id);
          if (!item.hasAttribute('aria-expanded')) item.setAttribute('aria-expanded', 'false');
          if (product.isConnected) item.removeAttribute('aria-haspopup');
          else item.setAttribute('aria-haspopup', 'dialog');
        }
      }

      const apps = [...(catalog.currentProduct ? [catalog.currentProduct]: []), ...catalog.connectedProducts];
      const place = (group, products, offset = 0) => {
        products.forEach(({ id }, index) => {
          const item = this._items.get(id);
          const atIndex = group.children[index + offset];
          if (atIndex !== item) group.insertBefore(item, atIndex ?? null);
        });
      };
      place(this._apps, apps);
      place(this._discover, catalog.discoverProducts, 1);
      this._apps.hidden = apps.length === 0;
      this._discover.hidden = catalog.discoverProducts.length === 0;
      this._brandSeparator.hidden = catalog.visibleProducts.length === 0;
      this._discoverSeparator.hidden = apps.length === 0 || catalog.discoverProducts.length === 0;

      if (replacementFocus?.getClientRects().length) replacementFocus.focus({ preventScroll: true });
      else if (restoreBarFocus) {
        if (this._compactQuery.matches) this._restoreInvoker();
        else this._bar.focus({ preventScroll: true });
      } else if (focused?.isConnected && this._bar.contains(focused) && !focused.getClientRects().length) this._restoreInvoker();
      else if (focused?.isConnected && this.shadowRoot.activeElement !== focused) focused.focus({ preventScroll: true });
      this._preview.update(catalog, t);
      this._panels.update(catalog, t);
    }

    _restoreInvoker(trigger) {
      const fallback = this._compactQuery.matches ? this._compact.others
       : trigger?.isConnected ? this._allProducts: this._bar;
      const target = [trigger, fallback, this._bar].find((node) => node?.isConnected && node.getClientRects().length);
      target?.focus({ preventScroll: true });
    }

    connectedCallback() {
      if (this._disposed) return;
      this._listenResponsive(true);
      this._agentOnboarding.connect();
      this._handleResponsiveChange();
    }

    _listenResponsive(enabled) {
      if (this._watchingResponsive === enabled) return;
      this._watchingResponsive = enabled;
      const method = enabled ? 'addEventListener': 'removeEventListener';
      this._compactQuery[method]('change', this._handleResponsiveChange);
      this._mobileQuery[method]('change', this._handleResponsiveChange);
    }

    disconnectedCallback() {
      this._listenResponsive(false);
      this._agentOnboarding.disconnect();
      this._lastFocused = undefined;
      this._preview.close();
      this._panels.close({ reason: 'disconnect', restoreFocus: false });
    }

    setActionHandler(handler) { this._actionHandler = handler; }
    setPanelHandler(handler) { this._panelHandler = handler; }
    setTrackHandler(handler, onError) { this._trackHandler = handler; this._errorHandler = onError; }
    performAgentAction(action, event) { return this._agentOnboarding.activate(action, event); }
    openPanel(action, event) { this._panels.open(action, event); }
    closePanel(options) { this._panels.close(options); }

    getBarHeight() {
      return this.isConnected ? this._bar.getBoundingClientRect().height: 0;
    }

    dispose() {
      this._disposed = true;
      this._listenResponsive(false);
      for (const link of this.shadowRoot.querySelectorAll('a[href]')) link.removeAttribute('href');
      this._panels.dispose();
      this._preview.dispose();
      this._compact.dispose();
      this._agentOnboarding.dispose();
      this._bar.removeEventListener('click', this._handleClick);
      this._bar.removeEventListener('auxclick', this._handleClick);
      this.shadowRoot.removeEventListener('focusin', this._handleFocusIn);
      this.shadowRoot.removeEventListener('focusout', this._handleFocusOut);
      this._lastFocused = undefined;
      this._actionHandler = undefined;
      this._panelHandler = undefined;
      this._trackHandler = undefined;
      this._errorHandler = undefined;
      this._catalog = undefined;
      this._items.clear();
      this._bar.replaceChildren();
    }
  }

  window.customElements.define(tagName, PositiveNavElement);
}
